import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { passwordResetService } from '../services/passwordResetService';

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const { t, language } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  // Moved steps inside component to use translation
  const steps = [
    t('dialog.forgotPassword.steps.1'),
    t('dialog.forgotPassword.steps.2'),
    t('dialog.forgotPassword.steps.3')
  ];

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError(null);
    setError(null);
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);
    setOtpError(null);
    setError(null);
  };

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    setPasswordError(null);
    setError(null);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    setPasswordError(null);
    setError(null);
  };

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError(t('error.email.required'));
      return false;
    }
    if (!passwordResetService.validateEmail(email)) {
      setEmailError(t('error.email.invalid'));
      return false;
    }
    return true;
  };

  const validateOtp = (otp: string): boolean => {
    if (!otp) {
      setOtpError(t('error.otp.empty'));
      return false;
    }
    if (otp.length !== 6) {
      setOtpError(t('error.otp.length'));
      return false;
    }
    return true;
  };

  const validatePassword = (password: string, confirmPassword: string): boolean => {
    if (!password) {
      setPasswordError(t('error.newPassword.empty'));
      return false;
    }
    if (password.length < 8) {
      setPasswordError(t('rule.password.length'));
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError(t('error.password.mismatch'));
      return false;
    }
    return true;
  };

  const handleRequestOtp = async () => {
    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await passwordResetService.requestOtp(email, language);

      if (response.success) {
        setSuccess(t('success.codeSentDesc'));
        setActiveStep(1);
      } else {
        setError(response.message || t('error.connection'));
      }
    } catch (err: any) {
      const errorMessage = err.code === 'OTP_SEND_ERROR'
        ? t('error.otp.send')
        : (err instanceof Error ? err.message : t('error.connection'));
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!validateOtp(otp)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await passwordResetService.verifyOtp(email, otp, language);

      if (response.success && response.resetToken) {
        setResetToken(response.resetToken);
        setSuccess(t('success.codeVerified'));
        setActiveStep(2); // Aller à l'étape 3 (nouveau mot de passe)
      } else {
        setError(response.message || t('error.otp.invalid'));
      }
    } catch (err: any) {
      const errorMessage = err.code === 'OTP_SEND_ERROR'
        ? t('error.otp.send')
        : (err instanceof Error ? err.message : t('error.connection'));
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validatePassword(newPassword, confirmPassword)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await passwordResetService.resetPassword(email, resetToken, newPassword, language);

      if (response.success) {
        setSuccess(t('dialog.forgotPassword.success'));
        setIsPasswordReset(true);
        // Ne pas fermer automatiquement le dialogue - laisser l'utilisateur le fermer manuellement
      } else {
        setError(response.message || t('error.resetPassword.failed'));
      }
    } catch (err: any) {
      const errorMessage = err.code === 'OTP_SEND_ERROR'
        ? t('error.otp.send')
        : (err instanceof Error ? err.message : t('error.connection'));
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResetToken('');
    setError(null);
    setSuccess(null);
    setEmailError(null);
    setOtpError(null);
    setPasswordError(null);
    setIsPasswordReset(false);
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('dialog.forgotPassword.step1.desc')}
            </Typography>
            <TextField
              fullWidth
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              disabled={isLoading}
              sx={{ mb: 2 }}
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('dialog.forgotPassword.step2.desc')}
            </Typography>
            <TextField
              fullWidth
              label={t('label.verificationCode')}
              value={otp}
              onChange={(e) => handleOtpChange(e.target.value.replace(/\D/g, ''))}
              error={!!otpError}
              helperText={otpError}
              required
              disabled={isLoading}
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem'
                }
              }}
              sx={{ mb: 2 }}
            />
          </Box>
        );
      case 2:
        return (
          <Box>
            {isPasswordReset ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                  {t('dialog.forgotPassword.success')}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                  {t('dialog.forgotPassword.step3.desc')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('label.newPassword')}
                  type="password"
                  value={newPassword}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  required
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('label.confirmPassword')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                  error={!!passwordError}
                  helperText={passwordError}
                  required
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const renderStepActions = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <Button
              onClick={handleClose}
              disabled={isLoading}
              sx={{ minWidth: 100 }}
            >
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={handleRequestOtp}
              variant="contained"
              disabled={isLoading || !email}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                t('btn.sendCode')
              )}
            </Button>
          </>
        );
      case 1:
        return (
          <>
            <Button
              onClick={handleClose}
              disabled={isLoading}
              sx={{ minWidth: 100 }}
            >
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={handleVerifyOtp}
              variant="contained"
              disabled={isLoading || !otp}
              sx={{ minWidth: 100 }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('btn.verify')
              )}
            </Button>
          </>
        );
      case 2:
        return (
          <>
            {isPasswordReset ? (
              <Button
                onClick={handleClose}
                variant="contained"
                sx={{ minWidth: 100 }}
              >
                {t('btn.close')}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleClose}
                  disabled={isLoading}
                  sx={{ minWidth: 100 }}
                >
                  {t('btn.cancel')}
                </Button>
                <Button
                  onClick={handleResetPassword}
                  variant="contained"
                  disabled={isLoading || !newPassword || !confirmPassword}
                  sx={{ minWidth: 120 }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t('btn.reset')
                  )}
                </Button>
              </>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { }} // Empêche la fermeture en cliquant en dehors
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LockResetIcon color="primary" />
          <Typography variant="h6" component="div">
            {t('dialog.forgotPassword.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 3,
            '& .MuiStepLabel-root .Mui-completed': {
              color: '#4caf50',
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: '#1976d2',
            },
            '& .MuiStepLabel-root .Mui-disabled': {
              color: 'rgba(0,0,0,0.5)',
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && !isPasswordReset && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ px: 0, pt: 2 }}>
        {renderStepActions(activeStep)}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
